import React, { useState, useEffect } from 'react';
import axios from 'axios';
import configApi from '../../config';

const AdminVerification = ({ user }) => {
    const [pendingTerms, setPendingTerms] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [feedbackMessages, setFeedbackMessages] = useState([]);

    const token = localStorage.getItem("token");
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };

    useEffect(() => {
        fetchPendingVerifications();
    }, []);

    const fetchPendingVerifications = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${configApi.apiEndpoint}/api/Terms/PendingVerification`, config);
            setPendingTerms(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching pending verifications:', error);
            const errorMsgId = Date.now();
            setFeedbackMessages(prev => [...prev, { id: errorMsgId, type: 'danger', text: 'Nepodarilo sa načítať termíny na overenie.' }]);
            setTimeout(() => setFeedbackMessages(prev => prev.filter(m => m.id !== errorMsgId)), 5000);
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerify = async (termId, wasPresent) => {
        try {
            await axios.put(
                `${configApi.apiEndpoint}/api/Terms/${termId}/Verify`,
                wasPresent,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const verifiedTerm = pendingTerms.find(t => t.id === termId);
            const termNameFallback = verifiedTerm?.firstName || verifiedTerm?.lastName
                ? `${verifiedTerm.firstName || ''} ${verifiedTerm.lastName || ''}`.trim()
                : 'Neznámy pacient';
            const patientName = verifiedTerm?.patient
                ? `${verifiedTerm.patient.firstName} ${verifiedTerm.patient.lastName} (${termNameFallback})`
                : termNameFallback;
            const termType = verifiedTerm?.termType?.name || 'Vlastný termín';
            const termDate = new Date(verifiedTerm.date).toLocaleDateString("sk-SK", { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

            // Remove the verified item from the queue
            setPendingTerms(prevTerms => prevTerms.filter(t => t.id !== termId));

            const msgId = Date.now() + Math.random();
            setFeedbackMessages(prev => [...prev, {
                id: msgId,
                type: wasPresent ? 'success' : 'danger',
                text: `${termDate} - Termín (${termType}) pre pacienta: ${patientName} bol označený ako ${wasPresent ? 'prítomný' : 'neprítomný'}.`
            }]);

            setTimeout(() => setFeedbackMessages(prev => prev.filter(m => m.id !== msgId)), 5000);

        } catch (error) {
            console.error('Verification error:', error);
            const errorMsgId = Date.now() + Math.random();
            setFeedbackMessages(prev => [...prev, { id: errorMsgId, type: 'danger', text: 'Chyba pri overovaní termínu.' }]);
            setTimeout(() => setFeedbackMessages(prev => prev.filter(m => m.id !== errorMsgId)), 5000);
        }
    };

    const handleVerifyBulk = async (termsInMonth, wasPresent) => {
        if (!window.confirm(`Skutočne chcete označiť všetky termíny (${termsInMonth.length}) v tomto mesiaci ako ${wasPresent ? 'prítomné' : 'neprítomné'}?`)) {
            return;
        }

        setIsLoading(true);
        try {
            const promises = termsInMonth.map(term =>
                axios.put(
                    `${configApi.apiEndpoint}/api/Terms/${term.id}/Verify`,
                    wasPresent,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        }
                    }
                )
            );

            await Promise.all(promises);

            const termIds = termsInMonth.map(t => t.id);
            setPendingTerms(prevTerms => prevTerms.filter(t => !termIds.includes(t.id)));

            const msgId = Date.now() + Math.random();
            setFeedbackMessages(prev => [...prev, {
                id: msgId,
                type: wasPresent ? 'success' : 'danger',
                text: `Hromadné overenie: ${termsInMonth.length} termínov bolo označených ako ${wasPresent ? 'prítomný' : 'neprítomný'}.`
            }]);

            setTimeout(() => setFeedbackMessages(prev => prev.filter(m => m.id !== msgId)), 5000);

        } catch (error) {
            console.error('Bulk verification error:', error);
            const errorMsgId = Date.now() + Math.random();
            setFeedbackMessages(prev => [...prev, { id: errorMsgId, type: 'danger', text: 'Chyba pri hromadnom overovaní.' }]);
            setTimeout(() => setFeedbackMessages(prev => prev.filter(m => m.id !== errorMsgId)), 5000);
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString("sk-SK", {
            hour: "2-digit",
            minute: "2-digit",
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
        });
    };

    return (
        <div className="container-fluid py-3">
            <h2 className="mb-4 d-flex align-items-center gap-2">
                <i className="bi bi-clipboard-check text-primary"></i>
                Overenie prítomnosti
            </h2>

            {feedbackMessages.length > 0 && (
                <div className="mb-4 d-flex flex-column gap-2">
                    {feedbackMessages.map(msg => (
                        <div key={msg.id} className={`alert alert-${msg.type} shadow-sm border-0 d-flex align-items-center gap-3 fw-bold fs-6 mb-0`} role="alert">
                            <i className={`bi fs-4 ${msg.type === 'success' ? 'bi-check-circle-fill text-success' : 'bi-exclamation-triangle-fill text-danger'}`}></i>
                            {msg.text}
                        </div>
                    ))}
                </div>
            )}

            {isLoading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Načítavam...</span>
                    </div>
                </div>
            ) : pendingTerms.length === 0 ? (
                <div className="alert alert-success d-flex align-items-center gap-3 py-4">
                    <i className="bi bi-check-circle-fill fs-2"></i>
                    <div>
                        <h5 className="mb-1">Všetko je aktuálne!</h5>
                        <p className="mb-0">Nemáte žiadne minulé termíny, ktoré by čakali na overenie prítomnosti.</p>
                    </div>
                </div>
            ) : (
                <div className="d-flex flex-column gap-3">
                    {Object.entries(
                        pendingTerms.reduce((acc, term) => {
                            const date = new Date(term.date);
                            const monthYear = date.toLocaleDateString("sk-SK", { month: "long", year: "numeric" });
                            const capitalizedMonthYear = monthYear.charAt(0).toUpperCase() + monthYear.slice(1);

                            if (!acc[capitalizedMonthYear]) {
                                acc[capitalizedMonthYear] = [];
                            }
                            acc[capitalizedMonthYear].push(term);
                            return acc;
                        }, {})
                    ).map(([monthYear, termsInMonth]) => (
                        <div key={monthYear} className="month-group">
                            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-2 border-bottom pb-2">
                                <h5 className="fw-bold mb-2 mb-md-0 d-flex align-items-center gap-2 text-dark">
                                    <i className="bi bi-calendar3 text-primary"></i>
                                    {monthYear}
                                    <span className="badge bg-secondary rounded-pill fs-6 ms-2 shadow-sm">{termsInMonth.length}</span>
                                </h5>
                                <div className="d-flex gap-2">
                                    <button
                                        onClick={() => handleVerifyBulk(termsInMonth, true)}
                                        className="btn btn-sm btn-success rounded-pill fw-bold px-3 shadow-sm d-flex align-items-center gap-1"
                                    >
                                        <i className="bi bi-check-all fs-6"></i> Všetci
                                    </button>
                                </div>
                            </div>
                            {/* DESKTOP TABLE VIEW (Hidden on Mobile) */}
                            <div className="d-none d-lg-block">
                                <div className="table-responsive bg-white rounded-4 shadow-sm border-0">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th scope="col" className="text-muted fw-semibold py-3 ps-4" style={{ width: '15%' }}>Dátum a Čas</th>
                                                <th scope="col" className="text-muted fw-semibold py-3" style={{ width: '25%' }}>Pacient</th>
                                                <th scope="col" className="text-muted fw-semibold py-3" style={{ width: '30%' }}>Typ Termínu</th>
                                                <th scope="col" className="text-muted fw-semibold py-3 text-center" style={{ width: '10%' }}>Trvanie</th>
                                                <th scope="col" className="text-muted fw-semibold py-3 pe-4 text-end" style={{ width: '20%' }}>Overenie</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {termsInMonth.map(term => (
                                                <tr key={term.id}>
                                                    <td className="ps-4">
                                                        <div className="d-flex flex-column">
                                                            <span className="fw-bold text-dark">
                                                                {new Date(term.date).toLocaleDateString("sk-SK", { day: "2-digit", month: "short", year: "numeric" })}
                                                            </span>
                                                            <span className="text-primary font-monospace fw-bold small bg-primary bg-opacity-10 rounded px-2 py-1 mt-1 d-inline-block" style={{ width: 'fit-content' }}>
                                                                <i className="bi bi-clock me-1"></i>
                                                                {new Date(term.date).toLocaleTimeString("sk-SK", { hour: "2-digit", minute: "2-digit" })}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="bg-secondary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center text-secondary" style={{ width: '35px', height: '35px' }}>
                                                                <i className="bi bi-person-fill fs-5"></i>
                                                            </div>
                                                            <span className="fw-bold text-dark text-truncate" style={{ maxWidth: '200px' }}>
                                                                {term.patient
                                                                    ? `${term.patient.firstName} ${term.patient.lastName} (${term.firstName || term.lastName ? `${term.firstName || ''} ${term.lastName || ''}`.trim() : 'Bez mena'})`
                                                                    : (term.firstName || term.lastName ? `${term.firstName || ''} ${term.lastName || ''}`.trim() : 'Neznámy pacient')}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-column">
                                                            <span className="fw-semibold text-dark text-truncate" title={term.termType?.name || 'Vlastný termín'}>
                                                                <i className="bi bi-bookmark-fill text-primary me-2"></i>
                                                                {term.termType?.name || 'Vlastný termín'}
                                                            </span>
                                                            {term.userNotes && (
                                                                <span className="small bg-warning bg-opacity-10 text-dark rounded px-2 py-1 mt-1 text-truncate border border-warning border-opacity-25" title={term.userNotes} style={{ maxWidth: '250px' }}>
                                                                    <i className="bi bi-chat-text me-1 text-warning"></i> {term.userNotes}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <span className="badge bg-secondary bg-opacity-10 text-dark border rounded-pill py-2 px-3 fw-medium">
                                                            <i className="bi bi-hourglass-split me-1"></i>{term.duration}m
                                                        </span>
                                                    </td>
                                                    <td className="pe-4">
                                                        <div className="d-flex gap-2 justify-content-end">
                                                            <button
                                                                onClick={() => handleVerify(term.id, true)}
                                                                className="btn btn-sm btn-success rounded-pill fw-bold px-3 shadow-sm d-flex align-items-center gap-1"
                                                            >
                                                                <i className="bi bi-check-lg fs-6"></i> Prítomný
                                                            </button>
                                                            <button
                                                                onClick={() => handleVerify(term.id, false)}
                                                                className="btn btn-sm btn-danger rounded-pill fw-bold px-3 shadow-sm d-flex align-items-center gap-1"
                                                            >
                                                                <i className="bi bi-x-lg fs-6"></i> Neprišiel
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* MOBILE CARD VIEW (Hidden on Desktop) */}
                            <div className="d-block d-lg-none mt-3">
                                <div className="row g-3">
                                    {termsInMonth.map(term => (
                                        <div key={term.id} className="col-12 col-md-6">
                                            <div className="card shadow-sm h-100 border-0 rounded-4 overflow-hidden">
                                                <div className="card-body p-3 d-flex flex-column">
                                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="bg-primary bg-opacity-10 text-primary rounded px-2 py-1 text-center font-monospace fw-bold" style={{ minWidth: '45px' }}>
                                                                {new Date(term.date).toLocaleTimeString("sk-SK", { hour: "2-digit", minute: "2-digit" })}
                                                            </div>
                                                            <span className="text-muted small fw-medium">
                                                                {new Date(term.date).toLocaleDateString("sk-SK", { day: "2-digit", month: "long" })}
                                                            </span>
                                                        </div>
                                                        <span className="badge bg-secondary bg-opacity-10 text-dark border rounded-pill">
                                                            <i className="bi bi-hourglass-split me-1"></i>{term.duration} min
                                                        </span>
                                                    </div>

                                                    <div className="mb-3 flex-grow-1">
                                                        <h5 className="mb-2 fw-bold text-dark text-truncate" title={term.termType?.name || 'Vlastný termín'}>
                                                            <i className="bi bi-bookmark-fill text-primary me-2"></i>
                                                            {term.termType?.name || 'Vlastný termín'}
                                                        </h5>

                                                        <div className="d-flex align-items-center text-muted small mb-2 text-truncate">
                                                            <i className="bi bi-person-fill me-2 fs-6"></i>
                                                            <span className="fw-medium text-dark flex-grow-1 text-truncate">
                                                                {term.patient
                                                                    ? `${term.patient.firstName} ${term.patient.lastName} (${term.firstName || term.lastName ? `${term.firstName || ''} ${term.lastName || ''}`.trim() : 'Bez mena'})`
                                                                    : (term.firstName || term.lastName ? `${term.firstName || ''} ${term.lastName || ''}`.trim() : 'Neznámy pacient')}
                                                            </span>
                                                        </div>

                                                        {term.userNotes && (
                                                            <div className="small bg-warning bg-opacity-10 text-dark rounded px-2 py-1 mt-2 text-truncate border border-warning border-opacity-25" title={term.userNotes}>
                                                                <i className="bi bi-chat-text me-1 text-warning"></i> {term.userNotes}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="d-flex gap-2 mt-auto">
                                                        <button
                                                            onClick={() => handleVerify(term.id, true)}
                                                            className="btn btn-success flex-grow-1 fw-bold py-2 shadow-sm"
                                                        >
                                                            <i className="bi bi-check-lg me-1"></i> Prítomný
                                                        </button>
                                                        <button
                                                            onClick={() => handleVerify(term.id, false)}
                                                            className="btn btn-danger flex-grow-1 fw-bold py-2 shadow-sm"
                                                        >
                                                            <i className="bi bi-x-lg me-1"></i> Neprišiel
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div >
    );
};

export default AdminVerification;
